import { useState, useEffect } from 'react';
import styled from 'styled-components';

const Square = styled.div<{
  isTopEnabled: boolean;
  isBottomEnabled: boolean;
  isLeftEnabled: boolean;
  isRightEnabled: boolean;
  borderWidth: number;
}>`
  position: relative;
  width: 8rem;
  height: 8rem;
  border-top: ${({ isTopEnabled }) =>
    `1px solid ${isTopEnabled ? 'black' : 'transparent'}`};
  border-bottom: ${({ isBottomEnabled }) =>
    `1px solid ${isBottomEnabled ? 'black' : 'transparent'}`};
  border-left: ${({ isLeftEnabled }) =>
    `1px solid ${isLeftEnabled ? 'black' : 'transparent'}`};
  border-right: ${({ isRightEnabled }) =>
    `1px solid ${isRightEnabled ? 'black' : 'transparent'}`};
`;

const Button = styled.button`
  position: absolute;
  width: 80px;
  height: 30px;
  font-family: 'OptumSans', sans-serif;
`;

const TopButton = styled(Button)`
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
`;

const BottomButton = styled(Button)`
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
`;

const LeftButton = styled(Button)`
  top: 50%;
  left: -40px;
  transform: translateY(-50%) rotate(-90deg);
`;

const RightButton = styled(Button)`
  top: 50%;
  right: -40px;
  transform: translateY(-50%) rotate(90deg);
`;

interface BorderVisibilityPickerProps {
  updateBorderData: (borderData: BorderData) => void;
  widgetState: any;
  getBottomBorderData: (widgetState: any, currentTab: number) => boolean;
  getLeftBorderData: (widgetState: any, currentTab: number) => boolean;
  getRightBorderData: (widgetState: any, currentTab: number) => boolean;
  getTopBorderData: (widgetState: any, currentTab: number) => boolean;
}

const BorderVisibilityPicker = ({
  widgetState,
  updateBorderData,
  getBottomBorderData,
  getLeftBorderData,
  getRightBorderData,
  getTopBorderData,
}: BorderVisibilityPickerProps) => {
  const { currentTab, borderData, hoverData } = widgetState.config;

  useEffect(() => {
    const data =
      currentTab === 0 || currentTab === undefined
        ? borderData
        : hoverData.borderData;

    const initializeBorderVisibility = borderData => ({
      ...borderData,
      isTopBorderVisible: borderData?.isTopBorderVisible ?? true,
      isBottomBorderVisible: borderData?.isBottomBorderVisible ?? true,
      isLeftBorderVisible: borderData?.isLeftBorderVisible ?? true,
      isRightBorderVisible: borderData?.isRightBorderVisible ?? true,
    });

    const updatedBorderData = initializeBorderVisibility(data);

    updateBorderData(updatedBorderData);
    setIsTopEnabled(updatedBorderData.isTopBorderVisible);
    setIsBottomEnabled(updatedBorderData.isBottomBorderVisible);
    setIsLeftEnabled(updatedBorderData.isLeftBorderVisible);
    setIsRightEnabled(updatedBorderData.isRightBorderVisible);
  }, [currentTab]);

  useEffect(() => {
    const isAnyBorderUndefined = data =>
      data?.isTopBorderVisible === undefined ||
      data?.isBottomBorderVisible === undefined ||
      data?.isLeftBorderVisible === undefined ||
      data?.isRightBorderVisible === undefined;

    if (
      isAnyBorderUndefined(borderData) ||
      isAnyBorderUndefined(hoverData?.borderData)
    ) {
      return;
    }
    setIsTopEnabled(getTopBorderData(widgetState, currentTab));
    setIsBottomEnabled(getBottomBorderData(widgetState, currentTab));
    setIsLeftEnabled(getLeftBorderData(widgetState, currentTab));
    setIsRightEnabled(getRightBorderData(widgetState, currentTab));
  }, [widgetState.config]);

  const [isTopEnabled, setIsTopEnabled] = useState(
    getTopBorderData(widgetState, currentTab)
  );
  const [isBottomEnabled, setIsBottomEnabled] = useState(
    getBottomBorderData(widgetState, currentTab)
  );
  const [isLeftEnabled, setIsLeftEnabled] = useState(
    getLeftBorderData(widgetState, currentTab)
  );
  const [isRightEnabled, setIsRightEnabled] = useState(
    getRightBorderData(widgetState, currentTab)
  );

  const handleTopChange = () => {
    setIsTopEnabled(!isTopEnabled);
    let data = currentTab === 1 ? hoverData.borderData : borderData;
    updateBorderData({
      ...data,
      isTopBorderVisible: !isTopEnabled,
      isBottomBorderVisible: isBottomEnabled,
      isLeftBorderVisible: isLeftEnabled,
      isRightBorderVisible: isRightEnabled,
    });
  };

  const handleBottomChange = () => {
    setIsBottomEnabled(!isBottomEnabled);
    let data = currentTab === 1 ? hoverData.borderData : borderData;
    updateBorderData({
      ...data,
      isBottomBorderVisible: !isBottomEnabled,
      isTopBorderVisible: isTopEnabled,
      isLeftBorderVisible: isLeftEnabled,
      isRightBorderVisible: isRightEnabled,
    });
  };

  const handleLeftChange = () => {
    setIsLeftEnabled(!isLeftEnabled);
    let data = currentTab === 1 ? hoverData.borderData : borderData;
    updateBorderData({
      ...data,
      isLeftBorderVisible: !isLeftEnabled,
      isTopBorderVisible: isTopEnabled,
      isBottomBorderVisible: isBottomEnabled,
      isRightBorderVisible: isRightEnabled,
    });
  };

  const handleRightChange = () => {
    setIsRightEnabled(!isRightEnabled);
    let data = currentTab === 1 ? hoverData.borderData : borderData;
    updateBorderData({
      ...data,
      isRightBorderVisible: !isRightEnabled,
      isTopBorderVisible: isTopEnabled,
      isBottomBorderVisible: isBottomEnabled,
      isLeftBorderVisible: isLeftEnabled,
    });
  };

  return (
    <Square
      data-test-id="square"
      isTopEnabled={isTopEnabled}
      isBottomEnabled={isBottomEnabled}
      isLeftEnabled={isLeftEnabled}
      isRightEnabled={isRightEnabled}
      borderWidth={borderData.thickness}
    >
      <TopButton
        id="top-border-button"
        data-test-id="top-border-button"
        onClick={handleTopChange}
      >
        Top
      </TopButton>
      <BottomButton
        id="bottom-border-button"
        data-test-id="bottom-border-button"
        onClick={handleBottomChange}
      >
        Bottom
      </BottomButton>
      <LeftButton
        id="left-border-button"
        data-test-id="left-border-button"
        onClick={handleLeftChange}
      >
        Left
      </LeftButton>
      <RightButton
        id="right-border-button"
        data-test-id="right-border-button"
        onClick={handleRightChange}
      >
        Right
      </RightButton>
    </Square>
  );
};

export default BorderVisibilityPicker;
